
import { supabase } from '../integrations/supabase/client';

export interface Target {
  id: string;
  session_id: string;
  user_id: string;
  target_code: string;
  target_type: 'location' | 'image' | 'random' | 'prediction';
  target_url: string;
  created_at: string;
  revealed_at?: string;
  is_favorite?: boolean;
  unsplash_data?: {
    id: string;
    photographer: string;
    photographerUsername: string;
    photographerUrl: string;
    unsplashUrl: string;
    downloadUrl: string;
    downloadEndpoint: string;
    description: string;
  };
}

export interface PredictionTarget {
  id: string;
  target_code: string;
  session_id: string;
  user_id: string;
  event_id: string;
  yes_image_url: string;
  no_image_url: string;
  yes_unsplash_data?: any;
  no_unsplash_data?: any;
  created_at: string;
  revealed_at?: string;
  is_favorite?: boolean;
  event?: {
    id: string;
    title: string;
    description?: string;
    close_time: string;
    yes_outcome_label: string;
    no_outcome_label: string;
    kalshi_event_id: string;
  };
}

export const targetService = {
  // Store a target from Assistant JSON response
  storeTarget: async (sessionId: string, targetCode: string, targetType: string, targetUrl: string, unsplashData?: any): Promise<Target | null> => {
    try {
      console.log('🎯 targetService: Storing target:', { sessionId, targetCode, targetType, targetUrl });
      
      // Get current user to ensure user_id is set
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !user) {
        console.error('❌ targetService: No authenticated user found:', userError);
        return null;
      }
      
      const insertData: any = {
        session_id: sessionId,
        user_id: user.id,
        target_code: targetCode,
        target_type: targetType,
        target_url: targetUrl
      };
      
      if (unsplashData) {
        insertData.unsplash_data = unsplashData;
      }
      
      const { data, error } = await supabase
        .from('targets')
        .insert(insertData)
        .select()
        .single();

      if (error) {
        console.error('❌ targetService: Error storing target:', error);
        return null;
      }

      console.log('✅ targetService: Target stored successfully:', data);
      return data as Target;
    } catch (error) {
      console.error('❌ targetService: Exception storing target:', error);
      return null;
    }
  },

  // Get target by session ID - checks both targets and prediction_targets tables
  getTargetBySession: async (sessionId: string): Promise<Target | PredictionTarget | null> => {
    try {
      console.log('🎯 targetService: Getting target for session:', sessionId);
      
      // First check regular targets table
      const { data: regularTarget, error: regularError } = await supabase
        .from('targets')
        .select('*')
        .eq('session_id', sessionId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      // Then check prediction_targets table
      const { data: predictionTarget, error: predictionError } = await supabase
        .from('prediction_targets')
        .select(`
          *,
          event:prediction_events(*)
        `)
        .eq('session_id', sessionId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (regularError && predictionError) {
        console.error('❌ targetService: Error getting targets:', regularError, predictionError);
        return null;
      }

      // Determine which target is more recent
      const regularTimestamp = regularTarget?.created_at ? new Date(regularTarget.created_at).getTime() : 0;
      const predictionTimestamp = predictionTarget?.created_at ? new Date(predictionTarget.created_at).getTime() : 0;

      if (predictionTimestamp > regularTimestamp && predictionTarget) {
        console.log('✅ targetService: Prediction target retrieved:', predictionTarget);
        // Transform to match expected interface
        return {
          ...predictionTarget,
          target_type: 'prediction' as const,
        } as PredictionTarget;
      }

      if (regularTarget) {
        console.log('✅ targetService: Regular target retrieved:', regularTarget);
        return regularTarget as Target;
      }

      console.log('⚠️ targetService: No target found for session');
      return null;
    } catch (error) {
      console.error('❌ targetService: Exception getting target:', error);
      return null;
    }
  },

  // Get prediction target by session
  getPredictionTargetBySession: async (sessionId: string): Promise<PredictionTarget | null> => {
    try {
      console.log('🎯 targetService: Getting prediction target for session:', sessionId);
      
      const { data, error } = await supabase
        .from('prediction_targets')
        .select(`
          *,
          event:prediction_events(*)
        `)
        .eq('session_id', sessionId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error('❌ targetService: Error getting prediction target:', error);
        return null;
      }

      if (!data) {
        console.log('⚠️ targetService: No prediction target found');
        return null;
      }

      console.log('✅ targetService: Prediction target retrieved:', data);
      return data as PredictionTarget;
    } catch (error) {
      console.error('❌ targetService: Exception getting prediction target:', error);
      return null;
    }
  },

  getTargetByCode: async (targetCode: string): Promise<Target | PredictionTarget | null> => {
    try {
      console.log('🎯 targetService: Getting target by code:', targetCode);
      
      // Check regular targets first
      const { data: regularTarget, error: regularError } = await supabase
        .from('targets')
        .select('*')
        .eq('target_code', targetCode)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (regularTarget) {
        console.log('✅ targetService: Regular target retrieved by code:', regularTarget);
        return regularTarget as Target;
      }

      // Check prediction targets
      const { data: predictionTarget, error: predictionError } = await supabase
        .from('prediction_targets')
        .select(`
          *,
          event:prediction_events(*)
        `)
        .eq('target_code', targetCode)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (predictionTarget) {
        console.log('✅ targetService: Prediction target retrieved by code:', predictionTarget);
        return {
          ...predictionTarget,
          target_type: 'prediction' as const,
        } as PredictionTarget;
      }

      if (regularError && predictionError) {
        console.error('❌ targetService: Error getting target by code:', regularError, predictionError);
      }

      return null;
    } catch (error) {
      console.error('❌ targetService: Exception getting target by code:', error);
      return null;
    }
  },

  markTargetRevealed: async (targetCode: string): Promise<boolean> => {
    try {
      console.log('🎯 targetService: Marking target as revealed:', targetCode);
      const revealedAt = new Date().toISOString();
      
      // Try regular targets first
      const { error: regularError, data: regularData } = await supabase
        .from('targets')
        .update({ revealed_at: revealedAt })
        .eq('target_code', targetCode)
        .select();

      if (regularData && regularData.length > 0) {
        console.log('✅ targetService: Regular target marked as revealed');
        return true;
      }

      // Also try prediction_targets table
      const { error: predictionError, data: predictionData } = await supabase
        .from('prediction_targets')
        .update({ revealed_at: revealedAt })
        .eq('target_code', targetCode)
        .select();

      if (predictionData && predictionData.length > 0) {
        console.log('✅ targetService: Prediction target marked as revealed');
        return true;
      }

      console.error('❌ targetService: Error marking target as revealed:', regularError, predictionError);
      return false;
    } catch (error) {
      console.error('❌ targetService: Exception marking target as revealed:', error);
      return false;
    }
  },

  getTargetForFeedback: async (targetCode: string): Promise<{ 
    target_url: string; 
    target_type: string;
    // Additional fields for prediction targets
    yes_image_url?: string;
    no_image_url?: string;
  } | null> => {
    try {
      console.log('🎯 targetService: Getting target details for feedback:', targetCode);
      
      // Check regular targets
      const { data: regularTarget } = await supabase
        .from('targets')
        .select('target_url, target_type')
        .eq('target_code', targetCode)
        .single();

      if (regularTarget) {
        console.log('✅ targetService: Target details retrieved for feedback:', regularTarget);
        return regularTarget;
      }

      // Check prediction targets - return BOTH images for proper comparison
      const { data: predictionTarget } = await supabase
        .from('prediction_targets')
        .select('yes_image_url, no_image_url')
        .eq('target_code', targetCode)
        .single();

      if (predictionTarget) {
        console.log('✅ targetService: Prediction target details for feedback:', predictionTarget);
        return {
          target_url: predictionTarget.yes_image_url,
          target_type: 'prediction',
          yes_image_url: predictionTarget.yes_image_url,
          no_image_url: predictionTarget.no_image_url
        };
      }

      console.log('⚠️ targetService: No target found for feedback');
      return null;
    } catch (error) {
      console.error('❌ targetService: Exception getting target for feedback:', error);
      return null;
    }
  },

  // Store user's prediction selection
  storeUserPrediction: async (
    sessionId: string, 
    targetId: string, 
    userSelectedOutcome: 'YES' | 'NO'
  ): Promise<boolean> => {
    try {
      console.log('🎯 targetService: Storing user prediction:', { sessionId, targetId, userSelectedOutcome });
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.error('❌ targetService: No authenticated user');
        return false;
      }

      const { error } = await supabase
        .from('user_predictions')
        .insert({
          session_id: sessionId,
          target_id: targetId,
          user_id: user.id,
          user_selected_outcome: userSelectedOutcome.toLowerCase(), // DB constraint requires lowercase
          submitted_at: new Date().toISOString()
        });

      if (error) {
        console.error('❌ targetService: Error storing prediction:', error);
        return false;
      }

      console.log('✅ targetService: User prediction stored');
      return true;
    } catch (error) {
      console.error('❌ targetService: Exception storing prediction:', error);
      return false;
    }
  }
};
